import { useEffect } from 'react';
import { ReactNode } from 'react';

/**
 * RadixPointerEventsCleanup Context
 *
 * PROBLEM:
 * When using Radix UI components (Dialog, DropdownMenu, etc.) with modal={true} (default),
 * Radix UI applies `pointer-events: none` to the `<body>` element to prevent interactions
 * with elements outside the modal/dropdown. This works correctly when a single modal is open.
 *
 * However, when multiple modals/dialogs are opened simultaneously (e.g., a DropdownMenu opens
 * a Dialog), Radix UI uses a reference counting mechanism tracked via the `data-scroll-locked`
 * attribute on the body element. If this reference count gets out of sync (due to race conditions,
 * errors, or improper cleanup), the `pointer-events: none` style can remain stuck on the body
 * element even after all modals are closed. This makes the entire application appear frozen/unresponsive
 * because users cannot interact with any elements.
 *
 * Additionally, when using modal={false} on DropdownMenu to avoid this issue, it causes another
 * problem: the DropdownMenu doesn't block pointer events, so clicks on the dropdown trigger inside
 * a Next.js Link component will bubble up and cause navigation, even when you only want to open
 * the dropdown.
 *
 * SOLUTION:
 * This context provider uses a MutationObserver to watch the `data-scroll-locked` attribute on
 * the body element. When the attribute value is 0 or not present (meaning no modals should be
 * locking the body), but `pointer-events: none` is still set, we clean it up. This prevents the
 * app from becoming unresponsive while still allowing Radix UI to properly manage pointer events
 * during normal operation.
 *
 * Ideally, we get rid of this context provider once we migrate to a new version of Radix UI that doesn't
 * have this issue. For now, it's a workaround to prevent the app from becoming unresponsive.
 */
export function RadixPointerEventsCleanup({ children }: { children: ReactNode }) {
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const body = document.body;

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type !== 'attributes' || mutation.attributeName !== 'data-scroll-locked') {
          return;
        }

        const total = parseInt(body.dataset.scrollLocked ?? '0', 10);
        if (total > 0) {
          return;
        }

        body.style.pointerEvents = '';
      }
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: ['data-scroll-locked'],
    });

    return () => observer.disconnect();
  }, []);

  return <>{children}</>;
}
